import type { Point2d } from ".."
import { isSamePoint } from ".."
import type { InsetEdge } from "./getInsetEdges"
import { trawl } from "../../matching/trawl"
import type { Edge2dComputed } from "../../mesh"

export const adjacentEdgesToPoints = (
  a: Edge2dComputed,
  b: Edge2dComputed,
): {
  p0: Point2d
  p1: Point2d
  p2: Point2d
} => {
  const teamA = [a.p1, a.p2]
  const teamB = [b.p1, b.p2]
  const result = trawl({
    teamA,
    teamB,
    fight: (
      pointA,
      pointB,
      { a: idxA, b: idxB },
    ): {
      p0: Point2d
      p1: Point2d
      p2: Point2d
    } | null => {
      if (isSamePoint(pointA, pointB)) {
        const otherA = teamA.find((_, i) => i !== idxA)
        const otherB = teamB.find((_, i) => i !== idxB)

        if (otherA === undefined || otherB === undefined) {
          throw new Error(`Absurd: Other array element not found.`)
        }

        return {
          p0: otherA,
          p1: pointA,
          p2: otherB,
        }
      }

      return null
    },
  })

  if (result === null) {
    throw new Error(`Absurd: Unexpected edge data found when linking edges.`)
  }

  return result
}

export const adjacentInsetEdgesToPoints = (
  a: InsetEdge,
  b: InsetEdge,
): {
  p0: { original: Point2d; inset0: Point2d }
  p1: { original: Point2d; inset1: Point2d; inset2: Point2d }
  p2: { original: Point2d; inset3: Point2d }
} => {
  const pointPairs: Record<
    `a` | `b`,
    [
      p1: { originalPoint: Point2d; insetPoint: Point2d },
      p2: { originalPoint: Point2d; insetPoint: Point2d },
    ]
  > = {
    a: [
      { originalPoint: a.original.p1, insetPoint: a.p1 },
      { originalPoint: a.original.p2, insetPoint: a.p2 },
    ],
    b: [
      { originalPoint: b.original.p1, insetPoint: b.p1 },
      { originalPoint: b.original.p2, insetPoint: b.p2 },
    ],
  }
  const result = trawl({
    teamA: pointPairs.a,
    teamB: pointPairs.b,
    fight: (
      pairA,
      pairB,
      { a: idxA, b: idxB },
    ): {
      p0: { original: Point2d; inset0: Point2d }
      p1: { original: Point2d; inset1: Point2d; inset2: Point2d }
      p2: { original: Point2d; inset3: Point2d }
    } | null => {
      if (isSamePoint(pairA.originalPoint, pairB.originalPoint)) {
        const otherA = pointPairs.a.find((_, i) => i !== idxA)
        const otherB = pointPairs.b.find((_, i) => i !== idxB)

        if (otherA === undefined || otherB === undefined) {
          throw new Error(
            `Absurd: Unexpected edge data found when linking edges.`,
          )
        }

        return {
          p0: { original: otherA.originalPoint, inset0: otherA.insetPoint },
          p1: {
            original: pairA.originalPoint,
            inset1: pairA.insetPoint,
            inset2: pairB.insetPoint,
          },
          p2: { original: otherB.originalPoint, inset3: otherB.insetPoint },
        }
      }

      return null
    },
  })
  if (result === null) throw new Error(`Unexpected edge data`)

  return result
}
